import { Link } from 'react-router-dom';
import { LogIn, LogOut, ShieldCheck, UserRound, Waypoints } from 'lucide-react';
import {
  inputClassName,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

function LoginView({
  backendUrl,
  loginUrl,
  currentUser,
  email,
  password,
  isSubmittingLogin,
  loginStatus,
  isFirebaseConfigured,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onLogout,
}) {
  const statusTone =
    loginStatus.type === 'warning'
      ? 'warning'
      : loginStatus.type === 'loading'
        ? 'loading'
        : loginStatus.type || 'neutral';

  return (
    <section className="w-full max-w-[1100px] text-app-ink">
      <PageHero
        eyebrow="Family Copilot"
        title="Sign in"
        lede="Use the same Firebase email and password account from the mobile app."
        bearyMessage={loginStatus.type === 'loading' ? 'Beary is checking your sign-in.' : undefined}
        bearyPreset="loading"
        action={<StatusPill tone={statusTone}>{loginStatus.message || 'Enter your email and password'}</StatusPill>}
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <SectionCard className="bg-app-shell">
          <SectionTitle
            title="Account access"
            copy="Firebase keeps the session available in this browser."
          />

          {!isFirebaseConfigured ? (
            <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              Firebase setup is required before login will work.
            </div>
          ) : null}

          <form className="mt-5 space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-app-ink" htmlFor="login-email">
                Email address
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => onEmailChange(event.target.value)}
                disabled={isSubmittingLogin}
                className={inputClassName}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-app-ink" htmlFor="login-password">
                Password
              </label>
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(event) => onPasswordChange(event.target.value)}
                disabled={isSubmittingLogin}
                className={inputClassName}
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
                type="submit"
                disabled={isSubmittingLogin}
              >
                <LogIn className="h-4 w-4" aria-hidden="true" />
                <span>{isSubmittingLogin ? 'Signing in...' : 'Continue'}</span>
              </button>

              {currentUser ? (
                <button
                  className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                  type="button"
                  onClick={onLogout}
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  <span>Logout</span>
                </button>
              ) : null}
            </div>

            <p className="text-sm text-app-muted">
              New here?{' '}
              <Link className="font-semibold text-app-accent-strong hover:underline" to="/signup">
                Create an account
              </Link>
            </p>
          </form>
        </SectionCard>

        <aside className="space-y-6">
          <SectionCard>
            <SectionTitle
              title="Saved session"
              copy="Visible account metadata stays unchanged."
            />
            <div className="mt-5 space-y-4">
              <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                    <UserRound className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                      Email
                    </p>
                    <p className="mt-1 text-sm font-medium text-app-ink">
                      {currentUser?.email || 'None yet'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                  Firebase UID
                </p>
                <p className="mt-2 break-all text-sm font-medium text-app-ink">
                  {currentUser?.firebaseUid || currentUser?.userId || 'Not loaded'}
                </p>
              </div>

              <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                  Static graph
                </p>
                <p className="mt-2 text-sm font-medium text-app-ink">
                  {currentUser?.featureFlags?.debugStaticGraph ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard>
            <SectionTitle
              title="Environment"
              copy="Backend visibility remains available on the login screen."
            />
            <div className="mt-5 space-y-4">
              <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                    <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                      Backend
                    </p>
                    <p className="mt-1 break-all text-sm font-medium text-app-ink">{backendUrl}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 text-orange-700">
                    <Waypoints className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                      Login endpoint
                    </p>
                    <p className="mt-1 break-all text-sm font-medium text-app-ink">{loginUrl}</p>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        </aside>
      </div>
    </section>
  );
}

export default LoginView;
